/**
 * Overview Page
 * Main overview page for AI CRM with summary cards and lead grid
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Pagination,
  Skeleton,
  Alert,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  PersonAdd,
  People,
  Stars,
  Assignment,
  FilterList,
  Settings as SettingsIcon
} from '@mui/icons-material';
import SummaryCard from '../components/SummaryCard';
import LeadCard from '../components/LeadCard';
import LeadFilterPanel from '../components/LeadFilterPanel';
import LeadForm, { LeadFormData } from '../components/LeadForm';
import LeadDetailDrawer from '../components/LeadDetailDrawer';
import ManagerDashboard from '../components/ManagerDashboard';
import { SettingsDrawer } from '../components/SettingsDrawer';
import { useDashboardSummary, useLeads, useLeadActivities } from '../hooks/useAICRM';
import { useCreateLead, useUpdateLead, useDeleteLead, useUpdateLeadStatus, useUpdateLeadPriority, useAddLeadActivity } from '../hooks/useLeadMutations';
import { usePermissions } from '../../../hooks/usePermissions';
import { showToast } from '../utils/toast';
import { LeadFilters, Lead } from '../types';
import { AddNoteFormData } from '../components/AddNoteForm';

const OverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { isOwner, isAdmin, isManager, role, companyRoleCode } = usePermissions();
  // The settings drawer holds platform-level config (Stripe keys, payment
  // cards). Only the SUPER_ADMIN of the whole platform should see it — NOT
  // the owner of an individual org, who is also marked OWNER by their
  // companyRoleCode but has no business touching platform Stripe keys.
  const isPlatformSuperAdmin = role === 'SUPER_ADMIN' || companyRoleCode === 'SUPER_ADMIN';
  const showManagerDashboard = isOwner || isAdmin || isManager;
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<LeadFilters>({});

  // Dialog states
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [leadFormMode, setLeadFormMode] = useState<'create' | 'edit'>('create');
  const [selectedLeadForEdit, setSelectedLeadForEdit] = useState<Lead | null>(null);
  const [selectedLeadForDetail, setSelectedLeadForDetail] = useState<Lead | null>(null);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);

  const pageSize = 12;
  const offset = (page - 1) * pageSize;

  const { data: summary, isLoading: summaryLoading } = useDashboardSummary();
  const { data: leadsData, isLoading: leadsLoading, error: leadsError } = useLeads({
    ...filters,
    limit: pageSize,
    offset,
  });

  // Fetch activities for selected lead
  const { data: leadActivities } = useLeadActivities(selectedLeadForDetail?.id || null);

  // Mutations
  const createLead = useCreateLead();
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();
  const updateLeadStatus = useUpdateLeadStatus();
  const updateLeadPriority = useUpdateLeadPriority();
  const addLeadActivity = useAddLeadActivity();

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFiltersChange = (newFilters: LeadFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleLeadClick = (lead: Lead) => {
    setSelectedLeadForDetail(lead);
  };

  const handleAddLead = () => {
    setLeadFormMode('create');
    setSelectedLeadForEdit(null);
    setLeadFormOpen(true);
  };

  const handleEditLead = (lead: Lead) => {
    setLeadFormMode('edit');
    setSelectedLeadForEdit(lead);
    setLeadFormOpen(true);
    setSelectedLeadForDetail(null);
  };

  const handleDeleteLead = async (leadId: string) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) {
      return;
    }

    try {
      await deleteLead.mutateAsync(leadId);
      showToast.leadDeleted();
      setSelectedLeadForDetail(null);
    } catch (error) {
      showToast.operationFailed('Failed to delete lead');
      console.error('Delete lead error:', error);
    }
  };

  const handleLeadFormSubmit = async (data: LeadFormData) => {
    try {
      if (leadFormMode === 'create') {
        await createLead.mutateAsync(data);
        showToast.leadCreated();
      } else if (selectedLeadForEdit) {
        await updateLead.mutateAsync({ id: selectedLeadForEdit.id, ...data });
        showToast.leadUpdated();
      }
    } catch (error) {
      showToast.operationFailed('Failed to save lead');
      console.error('Lead form submit error:', error);
      throw error;
    }
  };

  const handleUpdateStatus = async (leadId: string, newStatus: string) => {
    try {
      await updateLeadStatus.mutateAsync({ leadId, status: newStatus });
      showToast.statusUpdated();
    } catch (error) {
      showToast.operationFailed('Failed to update status');
      console.error('Update status error:', error);
    }
  };

  const handleUpdatePriority = async (leadId: string, newPriority: string) => {
    try {
      await updateLeadPriority.mutateAsync({ leadId, priority: newPriority });
      showToast.priorityUpdated();
    } catch (error) {
      showToast.operationFailed('Failed to update priority');
      console.error('Update priority error:', error);
    }
  };

  const handleAddActivity = async (leadId: string, activity: AddNoteFormData) => {
    try {
      await addLeadActivity.mutateAsync({ leadId, activity });
      showToast.activityAdded();
    } catch (error) {
      showToast.operationFailed('Failed to add activity');
      console.error('Add activity error:', error);
      throw error;
    }
  };

  const totalPages = leadsData?.pagination
    ? Math.ceil(leadsData.pagination.total / pageSize)
    : 0;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            AI CRM Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive customer relationship management and opportunity analysis platform
          </Typography>
        </Box>
        {/* Settings drawer is PLATFORM SUPER_ADMIN only — it exposes Stripe
            keys, payment cards, and other platform-level config. Even the
            owner of an individual org shouldn't see it. */}
        {isPlatformSuperAdmin && (
          <Tooltip title="Settings">
            <IconButton
              onClick={() => setSettingsDrawerOpen(true)}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Active Leads"
            value={summary?.activeLeads || 0}
            subtitle="Currently in pipeline"
            icon={<PersonAdd />}
            color="primary"
            loading={summaryLoading}
            onClick={() => navigate('/ai-crm/leads')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Customers"
            value={summary?.totalCustomers || 0}
            subtitle={`Out of ${(summary?.activeLeads || 0) + (summary?.totalCustomers || 0)} total contacts`}
            icon={<People />}
            color="info"
            loading={summaryLoading}
            onClick={() => navigate('/ai-crm/clients')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="High Score Leads"
            value={summary?.highScoreLeads || 0}
            subtitle="Score above 80"
            icon={<Stars />}
            color="success"
            loading={summaryLoading}
            onClick={() => navigate('/ai-crm/leads?minScore=80')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Pending Tasks"
            value={summary?.pendingTasks || 0}
            subtitle="Follow-ups needed"
            icon={<Assignment />}
            color="secondary"
            loading={summaryLoading}
            onClick={() => navigate('/ai-crm/tasks')}
          />
        </Grid>
      </Grid>

      {/* Manager Dashboard (visible for OWNER, ADMIN, MANAGER) */}
      {showManagerDashboard && (
        <>
          <Divider sx={{ my: 4 }} />
          <ManagerDashboard />
          <Divider sx={{ my: 4 }} />
        </>
      )}

      {/* Action Buttons */}
      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          sx={{ textTransform: 'none' }}
          onClick={handleAddLead}
        >
          Add Lead
        </Button>
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          sx={{ textTransform: 'none' }}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </Box>

      {/* Lead Cards Grid */}
      <Grid container spacing={3}>
        {/* Filter Panel (Sidebar - Left 3 columns) */}
        {showFilters && (
          <Grid item xs={12} md={3}>
            <LeadFilterPanel
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClear={handleClearFilters}
              variant="sidebar"
            />
          </Grid>
        )}

        {/* Lead Cards Grid (Main Content) */}
        <Grid item xs={12} md={showFilters ? 9 : 12}>
          {/* Header */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              AI-Powered Lead Management
            </Typography>
            {leadsData?.pagination && (
              <Typography variant="body2" color="text.secondary">
                Showing {offset + 1}-{Math.min(offset + pageSize, leadsData.pagination.total)} of {leadsData.pagination.total} leads
              </Typography>
            )}
          </Box>

          {/* Error State */}
          {leadsError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Error loading leads. Please try again.
            </Alert>
          )}

          {/* Loading Skeletons */}
          {leadsLoading && (
            <Grid container spacing={3}>
              {[...Array(pageSize)].map((_, index) => (
                <Grid item xs={12} sm={6} lg={showFilters ? 6 : 4} key={index}>
                  <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Lead Cards */}
          {!leadsLoading && leadsData && leadsData.data.length > 0 && (
            <Grid container spacing={3}>
              {leadsData.data.map((lead) => (
                <Grid item xs={12} sm={6} lg={showFilters ? 6 : 4} key={lead.id}>
                  <LeadCard
                    lead={lead}
                    onClick={handleLeadClick}
                    variant="detailed"
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Empty State */}
          {!leadsLoading && leadsData && leadsData.data.length === 0 && (
            <Box
              sx={{
                p: 6,
                textAlign: 'center',
                backgroundColor: 'rgba(0,0,0,0.02)',
                borderRadius: 1,
              }}
            >
              <PersonAdd sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No leads found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {Object.keys(filters).length > 0
                  ? 'Try adjusting your filters'
                  : 'Start adding leads to track opportunities'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={handleAddLead}
              >
                Add First Lead
              </Button>
            </Box>
          )}

          {/* Pagination */}
          {!leadsLoading && totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Lead Form Dialog */}
      <LeadForm
        open={leadFormOpen}
        mode={leadFormMode}
        initialData={selectedLeadForEdit || undefined}
        onClose={() => setLeadFormOpen(false)}
        onSubmit={handleLeadFormSubmit}
        isSubmitting={createLead.isPending || updateLead.isPending}
      />

      {/* Lead Detail Drawer */}
      <LeadDetailDrawer
        open={!!selectedLeadForDetail}
        lead={selectedLeadForDetail}
        activities={leadActivities || []}
        onClose={() => setSelectedLeadForDetail(null)}
        onUpdateStatus={handleUpdateStatus}
        onUpdatePriority={handleUpdatePriority}
        onAddActivity={handleAddActivity}
        onEdit={handleEditLead}
        onDelete={handleDeleteLead}
        isUpdating={updateLeadStatus.isPending || updateLeadPriority.isPending || addLeadActivity.isPending}
      />

      {/* Settings Drawer */}
      <SettingsDrawer
        open={settingsDrawerOpen}
        onClose={() => setSettingsDrawerOpen(false)}
      />
    </Container>
  );
};

export default OverviewPage;
